// @flow
import * as React from 'react';
import {
    ActiveIndicator,
    ActiveIndicatorWrapper,
    Description,
    DescriptionWrapper,
    ModeIcon,
    ModeItem,
    ModeItemWrapper,
    ModeSelector,
    TransitionWrapper
} from './styled';
import {
    ANIMATION_KEY_IMPLODE,
    ANIMATION_KEY_SLIDE,
    ANIMATION_IMPLODE_DURATION,
    ANIMATION_SLIDE_ENTER_DURATION,
    ANIMATION_SLIDE_EXIT_DURATION
} from './constants';

import type { AnyIcon } from '~/core/constants/icons';
import { get, noop } from 'lodash/fp';
import { injectIntl } from 'react-intl';
import messages from './intl';

type Props = {
    disabled?: boolean,
    intl?: $npm$ReactIntl$IntlShape,
    modes: Array<{
        description?: $npm$ReactIntl$MessageDescriptor | React$Element<*>,
        icons?: Array<AnyIcon>,
        key: string,
        name: $npm$ReactIntl$MessageDescriptor | string
    }>,
    onChange: noop,
    purpose: $npm$ReactIntl$MessageDescriptor,
    value: number
};

type State = {
    activeAnimate: boolean,
    descriptionUseImplodeAnimation: boolean
};

class ModeSelectorBlock extends React.Component<Props, State> {
    static defaultProps = {
        purpose: messages.purpose
    };

    state = {
        activeAnimate: false,
        descriptionUseImplodeAnimation: false
    };

    componentWillReceiveProps(nextProps: Props) {
        const { modes, value: prevValue } = this.props;
        const { value } = nextProps;
        const hasPrevDescription = !!get('description')(modes[prevValue]);
        const hasNextDescription = !!get('description')(modes[value]);
        const descriptionUseImplodeAnimation = hasPrevDescription && hasNextDescription;
        const activeAnimate = value !== prevValue ? !this.state.activeAnimate : this.state.activeAnimate;

        this.setState({ activeAnimate, descriptionUseImplodeAnimation });
    }

    render() {
        const { intl, modes, onChange, purpose, value, disabled } = this.props;
        const { formatMessage } = intl || {};
        const { activeAnimate, descriptionUseImplodeAnimation } = this.state;
        const descriptionAnimationClass = descriptionUseImplodeAnimation ? ANIMATION_KEY_IMPLODE : ANIMATION_KEY_SLIDE;
        const descriptionEnterDuration = descriptionUseImplodeAnimation
            ? ANIMATION_IMPLODE_DURATION
            : ANIMATION_SLIDE_ENTER_DURATION;
        const descriptionExitDuration =
            (descriptionUseImplodeAnimation ? ANIMATION_IMPLODE_DURATION : ANIMATION_SLIDE_EXIT_DURATION) - 50;

        const itemWidth = 100 / modes.length;
        const purposeValue = formatMessage(purpose);

        return (
            <ModeSelector aria-label={purposeValue} role="region">
                <ModeItemWrapper>
                    {modes.map(({ name, icons }, index) => {
                        const modeName = typeof name === 'object' && name.id ? formatMessage(name) : name;
                        const ariaLabel = typeof modeName === 'string' ? modeName : '';

                        return (
                            <ModeItem
                                aria-label={ariaLabel}
                                data-qa={`mode-selector-${ariaLabel.toLowerCase()}`}
                                disabled={disabled}
                                itemWidth={itemWidth}
                                key={index}
                                onClick={() => !disabled && onChange(index)}
                            >
                                {icons && icons.map((icon, i) => <ModeIcon key={`${icon}`} type={icon} />)}
                                {modeName}
                            </ModeItem>
                        );
                    })}
                    {value > -1 && (
                        <ActiveIndicatorWrapper itemCount={modes.length} itemWidth={itemWidth} selectedIndex={value}>
                            <ActiveIndicator activeAnimate={activeAnimate} disabled={disabled} />
                        </ActiveIndicatorWrapper>
                    )}
                </ModeItemWrapper>
                <DescriptionWrapper>
                    {modes.map(({ description }, index) => {
                        if (description) {
                            const descriptionValue =
                                typeof description === 'object' && description.id
                                    ? formatMessage(description)
                                    : description;
                            const ariaLabel = typeof descriptionValue === 'string' ? descriptionValue : '';

                            return (
                                <TransitionWrapper
                                    classNames={descriptionAnimationClass}
                                    in={index === value}
                                    key={`description-${index}`}
                                    mountOnEnter
                                    timeout={{
                                        enter: descriptionEnterDuration,
                                        exit: descriptionExitDuration
                                    }}
                                    unmountOnExit
                                >
                                    <div>
                                        <Description
                                            aria-label={ariaLabel}
                                            aria-live="polite"
                                            data-qa="mode-selector-description"
                                        >
                                            {descriptionValue}
                                        </Description>
                                    </div>
                                </TransitionWrapper>
                            );
                        }

                        return null;
                    })}
                </DescriptionWrapper>
            </ModeSelector>
        );
    }
}
//$FlowFixMe // Bug with intersection type of React$StatelessFunctionalComponent
export default injectIntl(ModeSelectorBlock);



// WEBPACK FOOTER //
// ./src/components/widgets/mode-selector-block/index.js